import { LazyService } from '@devim-front/service';
import { PayModel, PhoneModel } from '../types';
import { PaymentType } from '../../repay';
import { CompatService } from 'services/CompatService';
import { ServiceType } from 'services/RpcService/types/ServiceType';

export class Service extends LazyService {
  /**
   * Ключ для хранения токена оплаты в LS
   */
  private PAYMENT_TOKEN_KEY = 'payment_token';

  /**
   * Ключ для хранения выбранного типа платежа в LS
   * @private
   */
  private PAYMENT_TYPE_KEY = 'payment_type';

  /**
   * Ключ для хранения модели номера телефона
   */
  private PAYMENT_PHONE_MODEL_KEY = 'payment_phone_model';

  /**
   * Ключ для хранения суммы полного платежа в LS
   * @private
   */
  private PAYMENT_AMOUNT_FULL_KEY = 'payment_amount_full';

  /**
   * Ключ для хранения суммы минимального платежа в LS
   * @private
   */
  private PAYMENT_AMOUNT_MINIMAL_KEY = 'payment_amount_minimal';

  /**
   * Ключ для хранения суммы просроченного платежа в LS
   * @private
   */
  private PAYMENT_AMOUNT_EXPIRED_KEY = 'payment_amount_expired';

  /**
   * Ключ для хранения произвольной суммы платежа
   * @private
   */
  private PAYMENT_AMOUNT_ANY_KEY = 'payment_amount_any';

  /**
   * Ключ, по которому храниться флаг о просрочке в LS (0|1)
   * 0 - нет просрочки
   * 1 - есть просрочка
   * @private
   */
  private PAYMENT_IS_EXPIRED_KEY = 'payment_is_expired';

  /**
   * Ключ для хранения флага о статусе отправки
   * номера телефона на бэкенд  (0|1)
   * 0 - не отправлен
   * 1 - отправлен
   * @private
   */
  private PAYMENT_IS_PHONE_SEND = 'payment_is_phone_send';

  /**
   * Ключ для хранения флага о статусе отправки кода подтверждения
   * номера телефона на бэкенд  (0|1)
   * 0 - не отправлен
   * 1 - отправлен
   * @private
   */
  private PAYMENT_IS_CODE_SEND = 'payment_is_code_send';

  /**
   * Ключ для хранения флага о статусе отправки
   * номера займа на бэкенд  (0|1)
   * 0 - не отправлен
   * 1 - отправлен
   * @private
   */
  private PAYMENT_IS_LOAN_SEND = 'payment_is_loan_send';

  /**
   * Ключ для хранения флага о статусе выбора типа оплаты (0|1)
   * 0 - не отправлен
   * 1 - отправлен
   * @private
   */
  private PAYMENT_IS_TYPE_CHOSEN = 'payment_is_type_chosen';

  /**
   * Ссылка на rpc-сервис
   * @private
   */
  private get rpc() {
    return CompatService.get(this).rpcService;
  }

  /**
   * Возвращает токен платежа из LS
   */
  public readPaymentToken() {
    return this.readFromLS(this.PAYMENT_TOKEN_KEY) || '';
  }

  /**
   * Сохраняет токен платежа в LS
   * @param token
   */
  public savePaymentToken(token: string) {
    this.saveToLS(this.PAYMENT_TOKEN_KEY, token);
  }

  /**
   * Возвращает сумму полного платежа из LS
   */
  public readPaymentAmountFull(): number {
    return Number(this.readFromLS(this.PAYMENT_AMOUNT_FULL_KEY));
  }

  /**
   * Сохраняет сумму полного платежа в LS
   * @param amount
   */
  public savePaymentAmountFull(amount?: number): void {
    this.saveToLS(this.PAYMENT_AMOUNT_FULL_KEY, String(amount));
  }

  /**
   * Возвращает сумму минимального платежа из LS
   */
  public readPaymentAmountMinimal(): number {
    return Number(this.readFromLS(this.PAYMENT_AMOUNT_MINIMAL_KEY));
  }

  /**
   * Сохраняет сумму минимального платежа в LS
   * @param amount
   */
  public savePaymentAmountMinimal(amount?: number): void {
    this.saveToLS(this.PAYMENT_AMOUNT_MINIMAL_KEY, String(amount));
  }

  /**
   * Возвращает сумму просроченного платежа из LS
   */
  public readPaymentAmountExpired(): number {
    return Number(this.readFromLS(this.PAYMENT_AMOUNT_EXPIRED_KEY));
  }

  /**
   * Сохраняет сумму просроченного платежа в LS
   * @param amount
   */
  public savePaymentAmountExpired(amount?: number): void {
    this.saveToLS(this.PAYMENT_AMOUNT_EXPIRED_KEY, String(amount));
  }

  /**
   * Возвращает сумму произвольного платежа из LS
   */
  public readPaymentAmountAny(): number {
    return Number(this.readFromLS(this.PAYMENT_AMOUNT_ANY_KEY));
  }

  /**
   * Сохраняет сумму произвольного платежа в LS
   * @param amount
   */
  public savePaymentAmountAny(amount?: number): void {
    this.saveToLS(this.PAYMENT_AMOUNT_ANY_KEY, String(amount));
  }

  /**
   * Возвращает флаг о наличии просрочки из LS
   */
  public readPaymentIsExpired(): boolean {
    return Boolean(Number(this.readFromLS(this.PAYMENT_AMOUNT_EXPIRED_KEY)));
  }

  /**
   * Сохраняет флаг о наличии просрочки в LS
   * @param flag
   */
  public savePaymentIsExpired(flag: boolean): void {
    this.saveToLS(this.PAYMENT_IS_EXPIRED_KEY, String(Number(flag)));
  }

  /**
   * Возвращает флаг об отправке телефона на бэкенд из LS
   */
  public readPaymentIsPhoneSend(): boolean {
    return Boolean(Number(this.readFromLS(this.PAYMENT_IS_PHONE_SEND)));
  }

  /**
   * Сохраняет флаг об отправке телефона на бэкенд в LS
   * @param flag
   */
  public savePaymentIsPhoneSend(flag: boolean): void {
    this.saveToLS(this.PAYMENT_IS_PHONE_SEND, String(Number(flag)));
  }

  /**
   * Возвращает флаг об отправке кода подтверждения телефона на бэкенд из LS
   */
  public readPaymentIsCodeSend() {
    return Boolean(Number(this.readFromLS(this.PAYMENT_IS_CODE_SEND)));
  }

  /**
   * Сохраняет флаг об отправке кода подтверждения телефона на бэкенд в LS
   * @param flag
   */
  public savePaymentIsCodeSend(flag: boolean) {
    this.saveToLS(this.PAYMENT_IS_CODE_SEND, String(Number(flag)));
  }

  /**
   * Возвращает флаг об отправке номера займа на бэкенд из LS
   */
  public readPaymentIsLoanSend() {
    return Boolean(Number(this.readFromLS(this.PAYMENT_IS_LOAN_SEND)));
  }

  /**
   * Сохраняет флаг об отправке номера займа на бэкенд в LS
   * @param flag
   */
  public savePaymentIsLoanSend(flag: boolean) {
    this.saveToLS(this.PAYMENT_IS_LOAN_SEND, String(Number(flag)));
  }

  /**
   * Сохранение типа платежа в LS
   */
  public readPaymentType():
    | PaymentType.EARLY
    | PaymentType.NEXT
    | PaymentType.ANY {
    return this.readFromLS(this.PAYMENT_TYPE_KEY) as
      | PaymentType.EARLY
      | PaymentType.NEXT
      | PaymentType.ANY;
  }

  /**
   * Сохранение типа платежа в LS
   * @param paymentType
   */
  public savePaymentType(paymentType: PaymentType) {
    this.saveToLS(this.PAYMENT_TYPE_KEY, paymentType);
  }

  /**
   * Возвращает объект модели номера телефона
   */
  public readPaymentPhone() {
    const phoneString = this.readFromLS(this.PAYMENT_PHONE_MODEL_KEY);
    return phoneString
      ? JSON.parse(this.readFromLS(this.PAYMENT_PHONE_MODEL_KEY))
      : undefined;
  }

  /**
   * Сохранение модели телефона в LS
   * @param phone
   */
  public savePaymentPhone(phone?: PhoneModel) {
    this.saveToLS(this.PAYMENT_PHONE_MODEL_KEY, JSON.stringify(phone));
  }

  /**
   * Возвращает флаг выбран ли тип оплаты
   */
  public readPaymentIsTypeChosen() {
    return Boolean(Number(this.readFromLS(this.PAYMENT_IS_TYPE_CHOSEN)));
  }

  /**
   * Сохраняет флаг выбран ли тип оплаты
   * @param flag
   */
  public savePaymentIsTypeChosen(flag: boolean) {
    this.saveToLS(this.PAYMENT_IS_TYPE_CHOSEN, String(Number(flag)));
  }

  /**
   * Метод сохранения данных в LS
   * @param key
   * @param value
   * @private
   */
  private saveToLS(key: string, value?: string) {
    if (!value) return;
    window.localStorage.setItem(key, value);
  }

  /**
   * Метод чтения данных из LS
   * @param key
   * @private
   */
  private readFromLS(key: string): string {
    const value = window.localStorage.getItem(key);
    return value || '';
  }

  /**
   * Метод удаления данных из LS по ключу
   * @private
   */
  private removeFromLS(key: string) {
    window.localStorage.removeItem(key);
  }

  /**
   * Метод очистки используемых данных из LS
   */
  public clearCache() {
    this.removeFromLS(this.PAYMENT_AMOUNT_FULL_KEY);
    this.removeFromLS(this.PAYMENT_AMOUNT_MINIMAL_KEY);
    this.removeFromLS(this.PAYMENT_AMOUNT_EXPIRED_KEY);
    this.removeFromLS(this.PAYMENT_AMOUNT_ANY_KEY);
    this.removeFromLS(this.PAYMENT_IS_EXPIRED_KEY);
    this.removeFromLS(this.PAYMENT_IS_LOAN_SEND);
    this.removeFromLS(this.PAYMENT_IS_PHONE_SEND);
    this.removeFromLS(this.PAYMENT_IS_CODE_SEND);
    this.removeFromLS(this.PAYMENT_TOKEN_KEY);
    this.removeFromLS(this.PAYMENT_PHONE_MODEL_KEY);
    this.removeFromLS(this.PAYMENT_TYPE_KEY);
    this.removeFromLS(this.PAYMENT_IS_TYPE_CHOSEN);
  }

  /**
   * Метод загружает список доступных методов оплаты с сервера
   */
  public async getPaymentMethods(amount: number, token: string) {
    const response = await this.rpc.paymentGetMethodsByToken(
      ServiceType.Loan,
      amount,
      token,
    );

    return response;
  }

  /**
   * Отправка запроса на инфу об оплате по токену
   * @param pay
   * @param token
   */
  public async sendPay(pay: PayModel, token?: string) {
    const response = await this.rpc.paymentGetInfoByToken(
      pay.provider,
      pay.method,
      pay.amount,
      token!,
    );

    return response;
  }
}
